import React, { useState } from "react";
import {
  Table,
  Badge,
  ScrollArea,
  createStyles,
  Avatar,
  Text,
  Skeleton,
  Center,
  Stack,
  Group,
} from "@mantine/core";
import { useGetOrg } from "api/organizations/getOrg";
import { useUser } from "hooks/useUser";
import { useGetHierarchicalTeamMembers } from "api/users/getHierarchicalTeamMembers";

const useStyles = createStyles((theme) => ({
  header: {
    position: "sticky",
    top: 0,
    backgroundColor:
      theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
    transition: "box-shadow 150ms ease",
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      borderBottom: `1px solid ${
        theme.colorScheme === "dark"
          ? theme.colors.dark[3]
          : theme.colors.gray[2]
      }`,
    },
  },
  scrolled: {
    boxShadow: theme.shadows.sm,
  },
  employeeCell: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  employeeInfo: {
    display: "flex",
    flexDirection: "column",
  },
  employeeName: {
    fontSize: 14,
    fontWeight: 600,
  },
  employeeEmail: {
    fontSize: 12,
    color: theme.colors.gray[6],
  },
  departmentBadge: {
    backgroundColor: theme.colors.blue[0],
    color: theme.colors.blue[7],
    padding: "4px 8px",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    display: "inline-block",
  },
  statusBadge: {
    padding: "4px 8px",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    display: "inline-block",
  },
}));

// Define proper types for organization member
interface OrgMember {
  _id?: string;
  id?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  position?: string;
  role?: string;
  department?: {
    _id?: string;
    name?: string;
  };
  email?: string;
  corporateEmail?: string;
  personalEmail?: string;
  status?: string;
  employmentType?: string;
  employmentCategory?: string;
  profileImage?: string;
  reportingManager?: string;
  startDate?: string;
}

// Define type for mapped member with display data
interface MappedMember {
  id: string;
  name: string;
  email: string;
  position: string;
  department: string;
  status: string;
  employmentType: string;
  avatar: string | null;
  startDate: string;
}

// Helper function to get status badge color
const getStatusColor = (status = "Active"): string => {
  switch (status?.toLowerCase()) {
    case "active":
      return "green";
    case "inactive":
      return "gray";
    case "probation":
      return "yellow";
    case "terminated":
      return "red";
    default:
      return "blue";
  }
};

// Helper function to get employment type color
const getEmploymentTypeColor = (type = "full-time"): string => {
  switch (type?.toLowerCase()) {
    case "full-time":
    case "fulltime":
      return "teal";
    case "part-time":
    case "parttime":
      return "cyan";
    case "contract":
      return "violet";
    case "probation":
      return "orange";
    default:
      return "gray";
  }
};

// Format date to readable format
const formatDate = (dateString?: string): string => {
  if (!dateString) return "-";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "-";
  }
};

export function DirectorTeamMembers() {
  const { classes } = useStyles();
  const { data: hierarchicalTeamResponse, isLoading } = useGetHierarchicalTeamMembers();

  if (isLoading) {
    return (
      <ScrollArea>
        <Stack spacing="md">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={60} radius="md" />
          ))}
        </Stack>
      </ScrollArea>
    );
  }

  const orgMembers: OrgMember[] =
    hierarchicalTeamResponse?.data && Array.isArray(hierarchicalTeamResponse.data)
      ? (hierarchicalTeamResponse.data as OrgMember[])
      : [];

  if (orgMembers.length === 0) {
    return (
      <Center py="xl">
        <Text c="dimmed">No team members found</Text>
      </Center>
    );
  }

  // Map members to display format with proper typing
  const mapMember = (member: OrgMember): MappedMember => {
    const firstName = member.firstName ?? "";
    const lastName = member.lastName ?? "";
    const computedName = `${firstName} ${lastName}`.trim();
    const name = computedName || member.name || "Unknown";

    const idSrc = member._id ?? member.id ?? name;
    const id = String(idSrc);

    const position = member.position ?? member.role ?? "Team Member";
    const department = member.department?.name ?? "General";
    const email =
      member.corporateEmail ?? member.personalEmail ?? member.email ?? "-";
    const status = member.status ?? "Active";
    const employmentType = member.employmentType ?? "Full-time";
    const avatar = member.profileImage ?? null;
    const startDate = member.startDate ?? "";

    return {
      id,
      name,
      email,
      position,
      department,
      status,
      employmentType,
      avatar,
      startDate: formatDate(startDate),
    };
  };

  const members: MappedMember[] = orgMembers.map(mapMember);

  return (
    <ScrollArea>
      <Table style={{ minWidth: 800 }} verticalSpacing="sm">
        <thead className={classes.header}>
          <tr>
            <th>Employee</th>
            <th>Department</th>
            <th>Position</th>
            <th>Status</th>
            <th>Employment Type</th>
            <th>Start Date</th>
          </tr>
        </thead>
        <tbody>
          {members.map((member) => (
            <tr key={member.id}>
              <td>
                <div className={classes.employeeCell}>
                  <Avatar src={member.avatar} radius="xl" size="md">
                    {member.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()}
                  </Avatar>
                  <div className={classes.employeeInfo}>
                    <Text className={classes.employeeName}>{member.name}</Text>
                    <Text className={classes.employeeEmail}>
                      {member.email}
                    </Text>
                  </div>
                </div>
              </td>
              <td>
                <span className={classes.departmentBadge}>
                  {member.department}
                </span>
              </td>
              <td>
                <Text size="sm">{member.position}</Text>
              </td>
              <td>
                <Badge color={getStatusColor(member.status)} variant="light">
                  {member.status}
                </Badge>
              </td>
              <td>
                <Badge
                  color={getEmploymentTypeColor(member.employmentType)}
                  variant="light"
                >
                  {member.employmentType}
                </Badge>
              </td>
              <td>
                <Text size="sm">{member.startDate}</Text>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </ScrollArea>
  );
}
